import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle2, Globe, AlertTriangle, FileText, Scale, Info, ChevronRight, Lock, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

import { Badge } from './ui/badge';

/**
 * ESSConsentModal - Modal de consentement préalable pour les simulateurs ESS
 * Conforme aux exigences légales de l'Économie Sociale et Solidaire
 * 
 * @param {boolean} isOpen - Contrôle l'affichage du modal
 * @param {function} onAccept - Callback appelé lors de l'acceptation
 * @param {function} onClose - Callback pour fermer sans accepter
 * @param {string} simulatorId - Identifiant du simulateur pour le logging
 * @param {string} simulatorName - Nom du simulateur à afficher
 */
export default function ESSConsentModal({ 
  isOpen, 
  onAccept, 
  onClose, 
  simulatorId = "oscop_simulator_v1",
  simulatorName = "Simulateur ESS"
}) {
  const { t: t, i18n: i18n } = useTranslation();
  const [language, setLanguage] = useState('fr');
  const [isChecked, setIsChecked] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Détecter la langue du navigateur
  useEffect(() => {
    const browserLang = navigator.language?.split('-')[0] || 'fr';
    setLanguage(['fr', 'en'].includes(browserLang) ? browserLang : 'fr');
  }, []);

  // Reset checkbox when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
      setIsAnimating(true);
    }
  }, [isOpen]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const handleAccept = () => {
    if (!isChecked) return;

    // Log du consentement
    const consentLog = {
      accepted: true,
      datetime: new Date().toISOString(),
      language: language.toUpperCase(),
      simulator: simulatorId,
      userAgent: navigator.userAgent,
      timestamp: Date.now()
    };

    // Sauvegarder le consentement en localStorage
    try {
      const existingConsents = JSON.parse(localStorage.getItem('ess_consents') || '[]');
      existingConsents.push(consentLog);
      localStorage.setItem('ess_consents', JSON.stringify(existingConsents));
      localStorage.setItem(`ess_consent_${simulatorId}`, JSON.stringify(consentLog));
    } catch {
    }

    onAccept(consentLog);
  };

  if (!isOpen) return null;

  // Contenu FR
  const contentFR = {
    title: "Information préalable – Simulateur ESS",
    subtitle: simulatorName,
    paragraphs: [
      {
        icon: AlertTriangle,
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        text: <>Les résultats fournis par ce simulateur sont <strong>strictement indicatifs</strong>.</>
      },
      {
        icon: Shield,
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        text: <>Ce simulateur constitue un <strong>outil d'aide à la décision coopérative</strong>, mis à disposition dans un cadre relevant de l'<strong>Économie Sociale et Solidaire (ESS)</strong>.</>
      },
      {
        icon: Scale,
        color: "text-purple-600",
        bgColor: "bg-purple-50",
        text: <>Il <strong>ne constitue ni une offre, ni un devis, ni un engagement contractuel</strong>, ni une promesse de financement, ni une garantie, ni une opération d'assurance ou financière réglementée.</>
      },
      {
        icon: FileText,
        color: "text-green-600",
        bgColor: "bg-green-50",
        text: <>Toute adhésion, contribution ou engagement éventuel fait l'objet d'un <strong>processus distinct</strong> et de documents contractuels spécifiques.</>
      }
    ],
    checkboxLabel: "Je reconnais que ce simulateur est un outil d'aide à la décision ESS, sans valeur contractuelle, et j'accepte son utilisation dans les conditions décrites ci-dessus.",
    continueBtn: "Continuer vers le simulateur",
    legalNote: "Simulation indicative ESS – sans valeur contractuelle.",
    langSwitch: "English"
  };

  // Contenu EN
  const contentEN = {
    title: "Preliminary Information – ESS Simulator",
    subtitle: simulatorName,
    paragraphs: [
      {
        icon: AlertTriangle,
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        text: <>The results provided by this simulator are <strong>for indicative purposes only</strong>.</>
      },
      {
        icon: Shield,
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        text: <>This simulator is an <strong>ESS cooperative decision-support tool</strong>, made available within the framework of the <strong>Social and Solidarity Economy (SSE)</strong>.</>
      },
      {
        icon: Scale,
        color: "text-purple-600",
        bgColor: "bg-purple-50",
        text: <>It <strong>does not constitute an offer, a quotation, or a contractual commitment</strong>, nor a promise of financing, guarantee, insurance product, or regulated financial operation.</>
      },
      {
        icon: FileText,
        color: "text-green-600",
        bgColor: "bg-green-50",
        text: <>Any potential membership, contribution, or commitment is subject to a <strong>separate process</strong> and specific contractual documentation.</>
      }
    ],
    checkboxLabel: "I acknowledge that this ESS simulator is a decision-support tool with no contractual value, and I accept its use under the conditions described above.",
    continueBtn: "Continue to simulator",
    legalNote: "Indicative ESS simulation – no contractual value.",
    langSwitch: "Français"
  };

  const content = language === 'fr' ? contentFR : contentEN;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`
        relative w-full max-w-2xl max-h-[90vh] overflow-y-auto
        bg-white rounded-2xl shadow-2xl
        transform transition-all duration-500
        ${isAnimating ? 'animate-in fade-in zoom-in-95 duration-300' : ''}
      `}>
        {/* Header Premium */}
        <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-xl backdrop-blur">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold">{content.title}</h2>
                <p className="text-white/70 text-sm">{content.subtitle}</p>
              </div>
            </div>
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm">{content.langSwitch}</span>
            </button>
          </div>
          
          {/* Badges ESS */}
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-slate-800 text-white border-slate-600 font-semibold">
              <Sparkles className="h-3 w-3 mr-1" />
              Économie Sociale et Solidaire
            </Badge>
            <Badge className="bg-slate-800 text-white border-slate-600 font-semibold">
              <Lock className="h-3 w-3 mr-1" />
              Sans engagement
            </Badge>
            <Badge className="bg-slate-800 text-white border-slate-600 font-semibold">
              <Info className="h-3 w-3 mr-1" />
              Indicatif uniquement
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {content.paragraphs.map((para, index) => {
            const Icon = para.icon;
            return (
              <div 
                key={index}
                className={`flex items-start gap-4 p-4 rounded-xl ${para.bgColor} border border-transparent`}
              >
                <div className={`p-2 rounded-lg bg-white shadow-sm`}>
                  <Icon className={`h-5 w-5 ${para.color}`} />
                </div>
                <p className="text-gray-700 text-sm md:text-base leading-relaxed flex-1">
                  {para.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Consent Checkbox */}
        <div className="px-6 pb-4">
          <div className={`
            p-4 rounded-xl border-2 transition-all duration-300
            ${isChecked 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
            }
          `}>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                id="ess-consent"
                checked={isChecked}
                onCheckedChange={setIsChecked}
                className="mt-1 h-5 w-5 border-2"
              />
              <span className={`text-sm leading-relaxed ${isChecked ? 'text-green-800' : 'text-gray-600'}`}>
                {content.checkboxLabel}
              </span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6">
          <Button
            onClick={handleAccept}
            disabled={!isChecked}
            className={`
              w-full h-14 text-lg font-semibold rounded-xl
              transition-all duration-300
              ${isChecked 
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/25' 
                : 'bg-gray-300 cursor-not-allowed'
              }
            `}
          >
            {isChecked && <CheckCircle2 className="mr-2 h-5 w-5" />}
            {content.continueBtn}
            {isChecked && <ChevronRight className="ml-2 h-5 w-5" />}
          </Button>
        </div>

        {/* Footer Legal */}
        <div className="bg-gray-50 border-t px-6 py-4 rounded-b-2xl">
          <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-2">
            <Info className="h-3 w-3" />
            {content.legalNote}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook personnalisé pour gérer le consentement ESS
 * Vérifie si l'utilisateur a déjà donné son consentement pour ce simulateur
 */
export function useESSConsent(simulatorId) {
  const [hasConsent, setHasConsent] = useState(false);
  const [showModal, setShowModal] = useState(true);

  useEffect(() => {
    // Vérifier si un consentement existe déjà (session uniquement)
    const sessionConsent = sessionStorage.getItem(`ess_consent_session_${simulatorId}`);
    if (sessionConsent) {
      setHasConsent(true);
      setShowModal(false);
    }
  }, [simulatorId]);

  const handleAccept = (consentLog) => {
    // Sauvegarder pour cette session uniquement
    sessionStorage.setItem(`ess_consent_session_${simulatorId}`, JSON.stringify(consentLog));
    setHasConsent(true);
    setShowModal(false);
  };

  const resetConsent = () => {
    sessionStorage.removeItem(`ess_consent_session_${simulatorId}`);
    setHasConsent(false);
    setShowModal(true);
  };

  return {
    hasConsent,
    showModal,
    setShowModal,
    handleAccept,
    resetConsent
  };
}
